import { useEffect, useRef, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useToast } from '../components/Toast';
import { fmtVND, fmtDate, fmtDateTime } from '../utils/format';
import { debtDetail, debtGroup, sendDebtReport, listDebtReports, getDebtReport, debtByCustomer, getDomesticFees, scanDomesticFees, getDebtRecovery, saveDebtRecovery, scanRecoveryRevenue, getRecoveryScanStatus, closeDebtRecovery, scanFinanceFull, getFinanceScanStatus } from '../api/debts';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import WeeklyDebtReport from './WeeklyDebtReport';

const pct = (n) => `${((n || 0) * 100).toFixed(2).replace('.', ',')}%`;

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Màu chip cho từng loại phí.
const FEE_STYLE = {
  Viettel: { background: 'var(--danger-bg, #fde8e8)', color: 'var(--danger)' },
  Vnpost: { background: 'var(--accent-bg, #e6f1fb)', color: 'var(--accent)' },
  'NB-HCM': { background: 'var(--warn-bg, #fff4e0)', color: 'var(--warn)' },
  NVL: { background: '#efe9ff', color: '#6d4bd8' },
};

// Modal chi tiết công nợ cước/NVL của 1 KH (danh sách đơn/bill).
function DomesticCustomerModal({ cust, onClose }) {
  return (
    <Modal open onClose={onClose} wide>
      <h3 style={{ marginTop: 0 }}>
        <code style={{ color: 'var(--warn)' }}>{cust.custCode}</code> · {cust.custName} — còn nợ{' '}
        <b style={{ color: 'var(--danger)' }}>{fmtVND(cust.totalOwed)}</b>
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ fontSize: 13, minWidth: 620 }}>
          <thead>
            <tr>
              <th>Mã đơn / bill</th>
              <th>Loại phí</th>
              <th className="r">Tiền</th>
              <th>Ngày nợ</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {(cust.items || []).filter((it) => it.owed).map((it, i) => (
              <tr key={i}>
                <td><code>{it.code || '—'}</code></td>
                <td><span className="tag" style={{ ...FEE_STYLE[it.fee], fontSize: 11.5 }}>{it.fee}</span></td>
                <td className="r">{fmtVND(it.amount)}</td>
                <td>{it.debtDate ? fmtDate(it.debtDate) : <span className="muted">—</span>}</td>
                <td><span style={{ color: 'var(--danger)', fontWeight: 600 }}>● Chưa thanh toán</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// Tab "Công nợ cước nội địa và NVL".
function DomesticFeesTab({ toast }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(ymd(firstOfMonth));
  const [to, setTo] = useState(ymd(today));
  const [scanning, setScanning] = useState(false);
  const [cust, setCust] = useState(null);
  const [sortBy, setSortBy] = useState('days'); // 'days' | 'owed' | 'revenue' — mặc định nợ lâu nhất
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(false); // false = chỉ hiện KH nợ > 10 ngày
  const toggleSort = (k) => (sortBy === k ? setSortDir((d) => (d === 'desc' ? 'asc' : 'desc')) : (setSortBy(k), setSortDir('desc')));
  const arrow = (k) => (sortBy === k ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ⇅');

  const load = () => getDomesticFees().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  async function runScan() {
    setScanning(true);
    try {
      await scanDomesticFees(from, to);
      toast('Đã bắt đầu quét công nợ cước nội địa + NVL ở nền…');
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const d = await getDomesticFees().catch(() => null);
        if (d && d.status !== 'running') {
          setData(d);
          if (d.status === 'error') toast(`Lỗi quét: ${d.lastError || '?'}`, 'danger');
          break;
        }
      }
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi khi bắt đầu quét', 'danger');
    } finally {
      setScanning(false);
    }
  }

  const OVERDUE_DAYS = 10;
  const valOf = (c) => (sortBy === 'days' ? (c.maxDebtDays ?? -1) : sortBy === 'owed' ? c.totalOwed : c.totalRevenue);
  const rows = [...(data?.customers || [])].sort((a, b) => (sortDir === 'desc' ? valOf(b) - valOf(a) : valOf(a) - valOf(b)));
  const longRows = rows.filter((c) => (c.maxDebtDays ?? 0) > OVERDUE_DAYS); // KH nợ trên 10 ngày
  const shown = expanded ? rows : longRows;
  return (
    <>
      <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div className="small muted">
          {data?.syncedAt
            ? <>Quét: {fmtDateTime(data.syncedAt)} · {data.from ? `${fmtDate(data.from)} → ${fmtDate(data.to)}` : ''} · {rows.length} KH còn nợ · tổng nợ <b style={{ color: 'var(--danger)' }}>{fmtVND(data.totalOwed)}</b></>
            : 'Chưa quét lần nào.'}
          {data?.status === 'error' && <span style={{ color: 'var(--danger)' }}> · ⚠️ {data.lastError}</span>}
        </div>
        <div className="row" style={{ gap: 6 }}>
          <input type="date" style={{ width: 'auto' }} value={from} onChange={(e) => setFrom(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
          <span className="small muted" style={{ alignSelf: 'center' }}>→</span>
          <input type="date" style={{ width: 'auto' }} value={to} onChange={(e) => setTo(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
          <button className="btn sm ghost" disabled={scanning || data?.status === 'running'} onClick={runScan}>
            {scanning || data?.status === 'running' ? 'Đang quét…' : '🔄 Quét'}
          </button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="row" style={{ marginBottom: 8, gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="small muted">
            {expanded
              ? <>Đang hiện <b>toàn bộ {rows.length}</b> KH còn nợ</>
              : <>Đang hiện <b>{longRows.length}</b> KH nợ trên {OVERDUE_DAYS} ngày (ẩn {rows.length - longRows.length} KH nợ ≤ {OVERDUE_DAYS} ngày)</>}
          </span>
          <button className="btn sm ghost" onClick={() => setExpanded((v) => !v)}>
            {expanded ? `Chỉ KH nợ trên ${OVERDUE_DAYS} ngày` : '📋 Xem chi tiết toàn bộ KH'}
          </button>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ fontSize: 13, minWidth: 820 }}>
          <thead>
            <tr>
              <th>Mã KH</th>
              <th>Tên khách</th>
              <th>Sale</th>
              <th>Phí</th>
              <th className="r" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('revenue')}>Tổng doanh thu{arrow('revenue')}</th>
              <th className="r" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('owed')}>Còn nợ{arrow('owed')}</th>
              <th className="r" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('days')}>Nợ lâu nhất{arrow('days')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.length ? shown.map((c) => (
              <tr key={c.custCode}>
                <td><code style={{ color: 'var(--warn)' }}>{c.custCode}</code></td>
                <td className="cust-link" style={{ cursor: 'pointer' }} onClick={() => setCust(c)}>{c.custName || '—'}</td>
                <td>{c.sale || <span className="muted">—</span>}</td>
                <td>
                  {(c.fees || []).map((f) => (
                    <span key={f} className="tag" style={{ ...FEE_STYLE[f], fontSize: 11.5, marginRight: 4 }}>{f}</span>
                  ))}
                </td>
                <td className="r">{fmtVND(c.totalRevenue)}</td>
                <td className="r" style={{ color: 'var(--danger)', fontWeight: 600 }}>{fmtVND(c.totalOwed)}</td>
                <td className="r" style={{ fontWeight: c.maxDebtDays > 7 ? 700 : 400, color: c.maxDebtDays > 7 ? 'var(--danger)' : undefined }}>{c.maxDebtDays} ngày</td>
              </tr>
            )) : (
              <tr><td colSpan={7} className="empty">
                {rows.length ? `Không có KH nào nợ trên ${OVERDUE_DAYS} ngày — bấm "Xem chi tiết toàn bộ KH" để xem tất cả.` : 'Chưa có dữ liệu — bấm "Quét" để lấy từ SocSon.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {cust && <DomesticCustomerModal cust={cust} onClose={() => setCust(null)} />}
    </>
  );
}

// Tìm kiếm ĐÚNG DẤU (phượng ≠ phương), chỉ không phân biệt hoa/thường.
// Chuẩn hoá NFC để gõ tổ hợp/dựng sẵn đều khớp; giữ nguyên dấu thanh + chữ đ.
const norm = (s) =>
  String(s || '')
    .normalize('NFC')
    .toLowerCase()
    .trim();

// Modal: danh sách PXK còn nợ của 1 KH.
function CustomerDebtModal({ code, name, onClose }) {
  const [bills, setBills] = useState(null);
  useEffect(() => {
    debtByCustomer(code).then(setBills).catch(() => setBills([]));
  }, [code]);
  const totalDebt = (bills || []).reduce((a, b) => a + (b.debt || 0), 0);
  // Thời gian nợ 1 đơn = số ngày từ ngày duyệt (confirmDate, fallback ngày xuất) → mốc quét (syncedAt).
  // Cùng công thức với cột "Số ngày nợ" ở bảng tổng hợp.
  const daysOwed = (b) => {
    const basis = b.confirmDate || b.createdDate;
    if (!basis) return null;
    const asOf = b.syncedAt ? new Date(b.syncedAt) : new Date();
    return Math.max(0, Math.floor((asOf - new Date(basis)) / 86400000));
  };
  return (
    <Modal open onClose={onClose} wide>
      <h3 style={{ marginTop: 0 }}>PXK còn nợ — {name} ({code})</h3>
      {!bills ? (
        <div className="empty">Đang tải…</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ fontSize: 13, minWidth: 720 }}>
            <thead>
              <tr>
                <th>Mã PXK</th>
                <th className="c">Tuyến</th>
                <th className="r">Tổng tiền</th>
                <th className="r">Đã T.Toán</th>
                <th className="r">Còn nợ</th>
                <th>Ngày xuất</th>
                <th className="c">Thời gian nợ</th>
              </tr>
            </thead>
            <tbody>
              {bills.length ? (
                bills.map((b) => {
                  const d = daysOwed(b);
                  return (
                    <tr key={b._id}>
                      <td><code style={{ color: 'var(--warn)' }}>{b.code}</code></td>
                      <td className="c">{b.routeCode || '—'}</td>
                      <td className="r">{fmtVND(b.total)}</td>
                      <td className="r">{fmtVND(b.payment)}</td>
                      <td className="r"><b style={{ color: 'var(--danger)' }}>{fmtVND(b.debt)}</b></td>
                      <td>{b.createdDate ? fmtDate(b.createdDate) : b.confirmDate ? fmtDate(b.confirmDate) : '—'}</td>
                      <td className="c">
                        {d != null ? (
                          <span style={{ fontWeight: 700, color: d > 15 ? 'var(--danger)' : d > 7 ? 'var(--warn)' : 'inherit' }}>{d} ngày</span>
                        ) : (
                          <span className="small muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={7} className="empty">Không có PXK còn nợ.</td></tr>
              )}
            </tbody>
            {bills.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} className="r"><b>Tổng còn nợ</b></td>
                  <td className="r"><b style={{ color: 'var(--danger)' }}>{fmtVND(totalDebt)}</b></td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Modal>
  );
}

// Bảng công nợ chi tiết theo KH (tái dùng cho live + báo cáo đã chốt).
function DetailTable({ items, total }) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('debt');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(false); // false = chỉ KH nợ > 10 ngày
  const [pxk, setPxk] = useState(null); // { code, name } khi xem PXK của 1 KH
  const toggleSort = (k) => (sortBy === k ? setSortDir((d) => (d === 'desc' ? 'asc' : 'desc')) : (setSortBy(k), setSortDir('desc')));
  const arrow = (k) => (sortBy === k ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ' ⇅');
  const all = items || [];
  const longCount = all.filter((r) => (r.maxDebtDays || 0) > 10).length;
  const rows = all
    .filter((r) => (expanded || search.trim() ? true : (r.maxDebtDays || 0) > 10))
    .filter((r) => {
      if (!search.trim()) return true;
      const q = norm(search);
      return norm(r._id).includes(q) || norm(r.custName).includes(q) || norm(r.saleName).includes(q);
    })
    .sort((a, b) => {
      const va = sortBy === 'days' ? a.maxDebtDays ?? -1 : a.debt;
      const vb = sortBy === 'days' ? b.maxDebtDays ?? -1 : b.debt;
      return sortDir === 'desc' ? vb - va : va - vb;
    });
  return (
    <>
      <div className="row between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <b style={{ color: 'var(--danger)' }}>Tổng công nợ: {fmtVND(total || 0)}</b>
          <button className="btn sm ghost" onClick={() => setExpanded((v) => !v)}>
            {expanded ? '▲ Thu gọn' : '▼ Xem chi tiết'}
          </button>
          {!expanded && !search.trim() && <span className="small muted">Đang hiện {longCount} KH nợ &gt; 10 ngày</span>}
        </div>
        <input style={{ width: 240 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Tìm mã KH / tên / sale…" />
      </div>
      <div className="tbl-wrap framed">
        <table style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: 110 }}>Mã KH</th>
              <th>Tên KH</th>
              <th style={{ width: 160 }}>Sale</th>
              <th className="c" style={{ width: 70 }}>Số đơn</th>
              <th className="c" style={{ width: 120, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('days')}>
                Số ngày nợ{arrow('days')}
              </th>
              <th className="r" style={{ width: 150, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('debt')}>
                Công nợ{arrow('debt')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((r) => (
                <tr key={r._id}>
                  <td><code style={{ color: 'var(--warn)' }}>{r._id || '—'}</code></td>
                  <td className="cust-link" onClick={() => setPxk({ code: r._id, name: r.custName })}>
                    {r.custName || <span className="small muted">—</span>}
                  </td>
                  <td>{r.saleName || <span className="small muted">—</span>}</td>
                  <td className="c">{r.orders}</td>
                  <td className="c">
                    {r.maxDebtDays != null ? (
                      <span style={{ fontWeight: 700, color: r.maxDebtDays > 15 ? 'var(--danger)' : r.maxDebtDays > 7 ? 'var(--warn)' : 'inherit' }}>
                        {r.maxDebtDays} ngày
                      </span>
                    ) : (
                      <span className="small muted">—</span>
                    )}
                  </td>
                  <td className="r"><b style={{ color: 'var(--danger)' }}>{fmtVND(r.debt)}</b></td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={6} className="empty">Không có dữ liệu.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pxk && <CustomerDebtModal code={pxk.code} name={pxk.name} onClose={() => setPxk(null)} />}
    </>
  );
}

function GroupView({ groups, grandTotal }) {
  return (
    <>
      <b style={{ color: 'var(--danger)' }}>Tổng công nợ toàn công ty: {fmtVND(grandTotal || 0)}</b>
      {(groups || []).length ? (
        <div className="fin-group-grid">
        {groups.map((g) => (
          <div key={g.group} className="card" style={{ background: 'var(--panel2)', margin: 0 }}>
            <div className="row between" style={{ marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>👥 {g.group}</h3>
              <b style={{ color: 'var(--danger)' }}>{fmtVND(g.total)} · {g.orders} đơn</b>
            </div>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Sale</th>
                  <th className="c" style={{ width: 80 }}>Số đơn</th>
                  <th className="r" style={{ width: 150 }}>Công nợ</th>
                </tr>
              </thead>
              <tbody>
                {g.sales.map((s, i) => (
                  <tr key={i}>
                    <td>{s.saleName}{s.saleCode ? <span className="small muted"> ({s.saleCode})</span> : ''}</td>
                    <td className="c">{s.orders}</td>
                    <td className="r">{fmtVND(s.debt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        </div>
      ) : (
        <div className="empty" style={{ marginTop: 12 }}>Không có dữ liệu.</div>
      )}
    </>
  );
}

// Công nợ theo nhóm — gộp "tỷ lệ thu hồi theo chi nhánh" + "danh sách sale" thành 1 thẻ/chi nhánh.
function GroupRecoveryView({ groupData, custItems, toast, canClose }) {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 86400000);
  const [data, setData] = useState(null);
  const [saleView, setSaleView] = useState(null); // { saleName, saleCode } khi xem KH nợ của 1 sale
  const [rows, setRows] = useState([]); // { group, dauky, doanhthu, conlai }
  const [from, setFrom] = useState(ymd(weekAgo));
  const [to, setTo] = useState(ymd(today));
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const dirty = useRef(false);

  const load = () =>
    getDebtRecovery().then((d) => {
      setData(d);
      if (!dirty.current) setRows((d.rows || []).map((r) => ({ group: r.group, dauky: r.dauky, doanhthu: r.doanhthu, conlai: r.conlai })));
    });
  useEffect(() => { load().catch(() => {}); }, []);

  const closed = !!data?.closed;
  const setCell = (group, key, val) => {
    dirty.current = true;
    const n = Number(String(val).replace(/[^\d-]/g, '')) || 0;
    setRows((rs) => rs.map((r) => (r.group === group ? { ...r, [key]: n } : r)));
  };
  const comp = (r) => {
    const ps = (r.dauky || 0) + (r.doanhthu || 0);
    const dathu = ps - (r.conlai || 0);
    return { ps, dathu, rate: ps > 0 ? dathu / ps : 0 };
  };
  const tot = rows.reduce((a, r) => {
    const c = comp(r);
    a.dauky += r.dauky || 0; a.ps += c.ps; a.dathu += c.dathu; a.conlai += r.conlai || 0;
    return a;
  }, { dauky: 0, ps: 0, dathu: 0, conlai: 0 });
  const totRate = tot.ps > 0 ? tot.dathu / tot.ps : 0;

  // Ghép mỗi chi nhánh (recovery row) với danh sách sale + số đơn từ dữ liệu "theo nhóm".
  const gmap = {};
  (groupData?.groups || []).forEach((g) => { gmap[g.group] = g; });
  const cards = rows.map((r) => ({ r, g: gmap[r.group] || { sales: [], orders: 0 } }));
  // Nhóm chỉ có trong dữ liệu "theo nhóm" mà chưa có dòng thu hồi → vẫn hiện (chỉ đọc).
  (groupData?.groups || []).forEach((g) => {
    if (!rows.find((r) => r.group === g.group)) cards.push({ r: { group: g.group, dauky: 0, doanhthu: 0, conlai: g.total }, g, orphan: true });
  });
  // GTS luôn đứng đầu; các chi nhánh còn lại giữ nguyên thứ tự.
  cards.sort((a, b) => (a.r.group === 'GTS' ? -1 : b.r.group === 'GTS' ? 1 : 0));

  async function doScan() {
    setScanning(true);
    try {
      await scanRecoveryRevenue(from, to);
      toast('Đã bắt đầu quét doanh thu tuần (3 nguồn) ở nền…');
      for (let i = 0; i < 80; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await getRecoveryScanStatus().catch(() => null);
        if (s && s.scan?.status !== 'running') {
          if (s.scan?.status === 'error') toast(`Lỗi quét: ${s.scan.error || '?'}`, 'danger');
          else toast('Đã quét xong doanh thu tuần.');
          dirty.current = false;
          await load();
          break;
        }
      }
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi khi quét doanh thu', 'danger');
    } finally {
      setScanning(false);
    }
  }
  function pullAuto() {
    const s = data?.suggest || {};
    setRows((rs) => rs.map((r) => ({ ...r, dauky: s.dauky?.[r.group] ?? r.dauky, conlai: s.conlai?.[r.group] ?? r.conlai })));
    dirty.current = true;
    toast('Đã lấy lại đầu kỳ (tuần trước) + còn lại (từ quét công nợ). Nhớ bấm Lưu.');
  }
  async function save() {
    setBusy(true);
    try {
      await saveDebtRecovery(rows);
      dirty.current = false;
      toast('Đã lưu bảng thu hồi công nợ tuần.');
      await load();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi khi lưu', 'danger');
    } finally { setBusy(false); }
  }
  async function close() {
    if (!await confirm('Chốt bảng thu hồi công nợ tuần này? "Còn lại" sẽ thành "đầu kỳ" của tuần sau.')) return;
    setBusy(true);
    try {
      await saveDebtRecovery(rows); // lưu chỉnh sửa mới nhất trước khi chốt
      const r = await closeDebtRecovery();
      dirty.current = false;
      toast(`Đã chốt ${r.label}`);
      await load();
    } catch (e) {
      toast(e.response?.data?.message || 'Lỗi khi chốt', 'danger');
    } finally { setBusy(false); }
  }

  // Ô nhập số (căn phải, font mono) — khoá thì hiện text.
  const cell = (r, key, editable) =>
    closed || r.orphan || !editable ? (
      <div style={{ textAlign: 'right', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 8px', fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text3)' }}>
        {(r[key] || 0).toLocaleString('vi-VN')}
      </div>
    ) : (
      <input className="fin-merge-input" value={r[key] ? r[key].toLocaleString('vi-VN') : ''} onChange={(e) => setCell(r.group, key, e.target.value)} placeholder="0" />
    );

  const rateBadge = (rate, ps) =>
    ps ? (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: rate >= 0.7 ? 'var(--ok)' : 'var(--warn)', background: rate >= 0.7 ? 'color-mix(in srgb, var(--ok) 12%, transparent)' : 'color-mix(in srgb, var(--warn-fill) 18%, transparent)', padding: '2px 8px', borderRadius: 5 }}>{pct(rate)}</span>
    ) : (
      <span className="small muted">—</span>
    );

  return (
    <>
      {/* Toolbar: tiêu đề + tuần + khoảng ngày DT + hành động */}
      <div className="row between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
        <div className="row" style={{ gap: 11, alignItems: 'center' }}>
          <span style={{ width: 32, height: 32, borderRadius: 9, background: 'color-mix(in srgb, var(--accent) 12%, transparent)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', fontSize: 17 }}>📊</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Công nợ &amp; tỷ lệ thu hồi theo chi nhánh</div>
            <div className="small" style={{ color: 'var(--muted2)', marginTop: 1 }}>
              {data?.week?.label || '—'}{closed ? ' · 🔒 đã chốt' : ''}{data?.suggest?.prevLabel ? ` · đầu kỳ từ ${data.suggest.prevLabel}` : ''}
            </div>
          </div>
        </div>
        {!closed && (
          <div className="row" style={{ gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="small muted" style={{ fontWeight: 500 }}>DT tuần</span>
            <input type="date" style={{ width: 'auto' }} value={from} onChange={(e) => setFrom(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
            <span className="muted">→</span>
            <input type="date" style={{ width: 'auto' }} value={to} onChange={(e) => setTo(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
            <button className="btn sm ghost" disabled={scanning} onClick={doScan}>{scanning ? 'Đang quét…' : '🔄 Quét DT tuần'}</button>
            <button className="btn sm ghost" disabled={busy} onClick={pullAuto} title="Đầu kỳ (tuần trước) + Còn lại (từ quét công nợ)">↻ Lấy lại tự động</button>
            <button className="btn sm ghost" disabled={busy} onClick={save}>{busy ? '…' : '💾 Lưu'}</button>
            {canClose && <button className="btn sm" disabled={busy} onClick={close}>📌 Chốt tuần</button>}
          </div>
        )}
      </div>

      {/* Summary strip */}
      <div className="row" style={{ flexWrap: 'wrap', gap: 0, alignItems: 'center', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 12, padding: '15px 22px', marginBottom: 20 }}>
        <div style={{ paddingRight: 30 }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted2)', fontWeight: 500, marginBottom: 3 }}>Tổng công nợ toàn công ty</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 23, fontWeight: 700, color: 'var(--danger)', lineHeight: 1 }}>{fmtVND(tot.conlai)}</div>
        </div>
        <div style={{ padding: '0 30px', borderLeft: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted2)', fontWeight: 500, marginBottom: 3 }}>Đã thu tuần</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700, color: 'var(--ok)' }}>{tot.ps ? fmtVND(tot.dathu) : '—'}</div>
        </div>
        <div style={{ padding: '0 30px', borderLeft: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted2)', fontWeight: 500, marginBottom: 3 }}>Phát sinh tuần</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700 }}>{fmtVND(tot.ps)}</div>
        </div>
        <div style={{ padding: '0 30px', borderLeft: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted2)', fontWeight: 500, marginBottom: 3 }}>Tỷ lệ thu hồi chung</div>
          {rateBadge(totRate, tot.ps)}
        </div>
      </div>

      {/* Grid thẻ chi nhánh */}
      {cards.length ? (
        <div className="fin-merge-grid">
          {cards.map(({ r, g, orphan }, i) => {
            const c = comp(r);
            return (
              <div key={r.group} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
                {/* header */}
                <div style={{ padding: '13px 15px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--line)', minWidth: 0 }}>
                  <span style={{ width: 22, height: 22, borderRadius: 7, background: 'var(--panel2)', color: 'var(--muted2)', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>{i + 1}</span>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{r.group}</span>
                  <span style={{ fontSize: 11, color: 'var(--muted2)' }}>{g.orders || 0} đơn</span>
                </div>
                {/* recovery metrics */}
                <div style={{ padding: '13px 15px', background: 'color-mix(in srgb, var(--danger) 7%, transparent)', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 10.5, color: 'var(--danger)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Còn lại</span>
                    {rateBadge(c.rate, c.ps)}
                  </div>
                  {closed || orphan ? (
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: 'var(--danger)', lineHeight: 1.1 }}>{fmtVND(r.conlai)}</div>
                  ) : (
                    <input
                      value={r.conlai ? r.conlai.toLocaleString('vi-VN') : ''}
                      onChange={(e) => setCell(r.group, 'conlai', e.target.value)}
                      placeholder="0"
                      style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px dashed color-mix(in srgb, var(--danger) 30%, transparent)', borderRadius: 0, padding: '0 0 2px', fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 700, color: 'var(--danger)', lineHeight: 1.1 }}
                    />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid color-mix(in srgb, var(--danger) 15%, transparent)' }}>
                    <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Đã thu</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--ok)' }}>{c.ps ? fmtVND(c.dathu) : '—'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--muted2)', fontWeight: 500, marginBottom: 3 }}>Đầu kỳ</div>
                      {cell(r, 'dauky', true)}
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--muted2)', fontWeight: 500, marginBottom: 3 }}>+ DT tuần</div>
                      {cell(r, 'doanhthu', true)}
                    </div>
                  </div>
                </div>
                {/* sale list */}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 34px 96px', alignItems: 'center', padding: '8px 15px', fontSize: 10, fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--muted2)', borderBottom: '1px solid var(--line)' }}>
                  <div>Sale</div><div style={{ textAlign: 'center' }}>Đơn</div><div style={{ textAlign: 'right' }}>Công nợ</div>
                </div>
                {(() => {
                  // Thu gọn: chỉ hiện sale có công nợ ≥ 20tr; các sale nợ < 20tr ẩn đi (ghi chú số lượng).
                  const big = (g.sales || []).filter((s) => (s.debt || 0) >= 20_000_000);
                  const hidden = (g.sales || []).length - big.length;
                  const hint = hidden > 0 && (
                    <div className="small muted" style={{ padding: '8px 15px', textAlign: 'center' }}>Đã ẩn {hidden} sale nợ &lt; 20tr</div>
                  );
                  if (!big.length) {
                    return (
                      <div className="small muted" style={{ padding: '12px 15px', textAlign: 'center' }}>
                        {(g.sales || []).length ? `Tất cả ${hidden} sale đều nợ < 20tr (đã ẩn)` : 'Không có công nợ COD.'}
                      </div>
                    );
                  }
                  return (
                    <>
                      {big.map((s, j) => (
                        <div key={j} onClick={() => setSaleView({ saleName: s.saleName, saleCode: s.saleCode })} title="Xem KH đang nợ của sale này"
                          style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 34px 96px', alignItems: 'center', padding: '9px 15px', borderBottom: '1px solid var(--line)', background: j % 2 ? 'var(--panel2)' : 'transparent', cursor: 'pointer' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.saleName}</div>
                            {s.saleCode && <div style={{ fontSize: 10.5, fontFamily: 'var(--font-mono)', color: 'var(--accent)', marginTop: 1 }}>{s.saleCode}</div>}
                          </div>
                          <div style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text3)' }}>{s.orders}</div>
                          <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: 'var(--danger)' }}>{fmtVND(s.debt)}</div>
                        </div>
                      ))}
                      {hint}
                    </>
                  );
                })()}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty">Chưa có dữ liệu — bấm "Quét công nợ" ở đầu trang.</div>
      )}

      <div className="small muted" style={{ marginTop: 12, lineHeight: 1.5 }}>
        Phát sinh tuần = Đầu kỳ + Doanh thu tuần · Đã thu = Phát sinh − Còn lại · Tỷ lệ = Đã thu ÷ Phát sinh. Đầu kỳ = công nợ tuần trước theo BÁO CÁO ĐÃ CHỐT. Còn lại = quét công nợ + cước/NVL.
      </div>

      {(data?.history || []).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button className="btn sm ghost" onClick={() => setShowHist((v) => !v)}>{showHist ? '▲ Ẩn tuần đã chốt' : `▼ Xem ${data.history.length} tuần đã chốt`}</button>
          {showHist && data.history.slice().reverse().map((h) => (
            <div key={h.weekKey} className="tbl-wrap framed" style={{ marginTop: 10 }}>
              <div className="small muted" style={{ padding: '4px 8px' }}>📌 {h.label} — chốt bởi {h.closedBy || '—'} {h.closedAt ? `lúc ${fmtDateTime(h.closedAt)}` : ''}</div>
              <table style={{ fontSize: 12.5 }}>
                <thead><tr><th>Chi nhánh</th><th className="r">Đầu kỳ</th><th className="r">Phát sinh</th><th className="r">Đã thu</th><th className="r">Còn lại</th><th className="r">Tỷ lệ</th></tr></thead>
                <tbody>
                  {h.rows.map((r) => (
                    <tr key={r.group}>
                      <td><b>{r.group}</b></td>
                      <td className="r">{fmtVND(r.dauky)}</td>
                      <td className="r">{fmtVND(r.phatsinh)}</td>
                      <td className="r">{fmtVND(r.dathu)}</td>
                      <td className="r">{fmtVND(r.conlai)}</td>
                      <td className="r"><b>{pct(r.rate)}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {saleView && (() => {
        // Lọc danh sách công nợ chi tiết (theo KH) theo sale đã bấm — ưu tiên khớp mã sale.
        const items = (custItems || [])
          .filter((it) => (saleView.saleCode ? it.saleCode === saleView.saleCode : it.saleName === saleView.saleName))
          .sort((a, b) => (b.debt || 0) - (a.debt || 0));
        const total = items.reduce((a, it) => a + (it.debt || 0), 0);
        return (
          <Modal open onClose={() => setSaleView(null)}>
            <h3 style={{ marginTop: 0 }}>
              KH đang nợ · {saleView.saleName}{saleView.saleCode ? ` (${saleView.saleCode})` : ''} — {items.length} KH · <b style={{ color: 'var(--danger)' }}>{fmtVND(total)}</b>
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: 13, minWidth: 440 }}>
                <thead><tr><th>Mã KH</th><th>Tên KH</th><th className="r">Công nợ</th></tr></thead>
                <tbody>
                  {items.length ? items.map((it, i) => (
                    <tr key={i}>
                      <td><code style={{ color: 'var(--warn)' }}>{it._id}</code></td>
                      <td>{it.custName || <span className="muted">—</span>}</td>
                      <td className="r"><b style={{ color: 'var(--danger)' }}>{fmtVND(it.debt)}</b></td>
                    </tr>
                  )) : (
                    <tr><td colSpan={3} className="empty">Không có KH nợ trong bảng công nợ chi tiết.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Modal>
        );
      })()}
    </>
  );
}

// Ghi chú trạng thái quét công nợ đầy đủ (tự động 16:30 thứ 5 / thủ công) — để TP biết dữ liệu đã quét.
function ScanStatusNote({ s }) {
  if (!s || !s.status || s.status === 'idle') return null;
  const when = s.finishedAt || s.startedAt;
  const map = {
    running: { bg: 'var(--accent-bg, #e6f1fb)', color: 'var(--accent)', icon: '⏳', text: `Đang quét công nợ đầy đủ… ${s.byName ? `(${s.byName})` : ''}` },
    done: { bg: 'var(--ok-bg, #e6f6ec)', color: 'var(--ok, #1a7f45)', icon: '🟢', text: `${s.byName || 'Quét'} — quét công nợ thành công lúc ${when ? fmtDateTime(when) : '—'}.` },
    error: { bg: 'var(--danger-bg, #fdeceb)', color: 'var(--danger)', icon: '🔴', text: `${s.byName || 'Quét'} — quét LỖI lúc ${when ? fmtDateTime(when) : '—'}: ${s.lastError || '?'} — bấm "Quét công nợ" để thử lại.` },
  };
  const v = map[s.status] || map.done;
  return (
    <div style={{ background: v.bg, color: v.color, borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 500, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
      <span>{v.icon}</span><span>{v.text}</span>
    </div>
  );
}

export default function FinanceReportPage() {
  const [scanning, setScanning] = useState(false);
  const toast = useToast();
  const { me } = useAuth();
  const canCloseRecovery = me?.role === 'GD' || (me?.deptHeadOf || []).includes('Phòng kế toán');
  // Chỉ GĐ/CV + Trưởng phòng kế toán mới được bấm "Quét công nợ".
  const canScanDebt = me?.role === 'GD' || me?.role === 'CV' || (me?.deptHeadOf || []).includes('Phòng kế toán');
  const [tab, setTab] = useState('report'); // 'report' | 'detail' | 'group'
  const [detail, setDetail] = useState(null);
  const [group, setGroup] = useState(null);
  const [reports, setReports] = useState([]);
  const [active, setActive] = useState(null); // báo cáo tuần đã chốt đang xem (modal)
  const [custModal, setCustModal] = useState(null); // { code, name } — COD/KH đang xem
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [sending, setSending] = useState(false);
  const [scanStatus, setScanStatus] = useState(null); // trạng thái quét công nợ đầy đủ (auto/thủ công)
  const scroller = useRef(null);

  const loadScanStatus = () => getFinanceScanStatus().then(setScanStatus).catch(() => {});
  function loadLive() {
    return Promise.all([debtDetail(), debtGroup()]).then(([d, g]) => {
      setDetail(d);
      setGroup(g);
    });
  }
  function loadReports() {
    return listDebtReports().then((list) => setReports(list));
  }
  function reload() {
    setLoading(true);
    loadScanStatus();
    Promise.all([loadLive(), loadReports()])
      .then(() => setDenied(false))
      .catch((err) => {
        if (err.response?.status === 403) setDenied(true);
      })
      .finally(() => setLoading(false));
  }
  useEffect(reload, []);
  // Quay lại tab/cửa sổ -> tải lại danh sách báo cáo, để báo cáo vừa bị GĐ "Xoá / Làm lại"
  // (ở màn Báo cáo tuần) TỰ biến khỏi dải tuần mà không cần F5.
  useEffect(() => {
    const onFocus = () => { loadReports().catch(() => {}); loadScanStatus(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, [reports]);

  // Quét công nợ ĐẦY ĐỦ trên server: COD + cước nội địa/NVL + doanh thu tuần (dùng token SocSon).
  // Chạy nền; poll trạng thái tới khi xong rồi tải lại số liệu. (Cùng lệnh với auto 16:30 thứ 5.)
  async function scan() {
    setScanning(true);
    try {
      await scanFinanceFull();
      toast('Đã bắt đầu quét công nợ đầy đủ (COD + cước/NVL + doanh thu tuần) ở nền…');
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await getFinanceScanStatus().catch(() => null);
        if (s) setScanStatus(s);
        if (s && s.status !== 'running') {
          if (s.status === 'error') toast(`Lỗi quét: ${s.lastError || '?'}`, 'danger');
          else toast('Đã quét xong công nợ đầy đủ.');
          break;
        }
      }
      await loadLive();
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi khi bắt đầu quét', 'danger');
    } finally {
      setScanning(false);
    }
  }
  async function send() {
    if (!await confirm('Chốt báo cáo công nợ tuần này? Dữ liệu sẽ được lưu cố định.')) return;
    setSending(true);
    try {
      const r = await sendDebtReport();
      toast(`Đã chốt báo cáo ${r.label}`);
      await loadReports();
      setTab('report');
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi chốt báo cáo', 'danger');
    } finally {
      setSending(false);
    }
  }
  async function openReport(r) {
    const full = await getDebtReport(r._id);
    setActive(full);
  }

  if (denied) {
    return (
      <>
        <Head />
        <div className="card">
          <div className="empty" style={{ padding: 40, textAlign: 'center' }}>
            🔒 Chỉ <b>Giám đốc</b> và <b>Phòng kế toán</b> xem được Báo cáo tài chính.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Head />
      <div className="card">
        <div className="row between" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div className="seg">
            <button className={tab === 'report' ? 'active' : ''} onClick={() => setTab('report')}>Báo cáo tuần</button>
            <button className={tab === 'detail' ? 'active' : ''} onClick={() => setTab('detail')}>Công nợ chi tiết</button>
            <button className={tab === 'group' ? 'active' : ''} onClick={() => setTab('group')}>Công nợ theo nhóm</button>
          </div>
          {tab !== 'report' && (
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {detail?.syncedAt && (
                <span className="small muted">
                  Quét: {fmtDateTime(detail.syncedAt)}{detail.from ? ` · ${fmtDate(detail.from)} → ${fmtDate(detail.to)}` : ''}
                </span>
              )}
              {canScanDebt && (
                <button className="btn sm ghost" disabled={scanning} onClick={scan}>
                  {scanning ? 'Đang quét…' : '🔄 Quét công nợ'}
                </button>
              )}
              <button className="btn sm" disabled={sending} onClick={send}>
                {sending ? 'Đang chốt…' : '📤 Gửi báo cáo (chốt tuần)'}
              </button>
            </div>
          )}
        </div>

        <ScanStatusNote s={scanStatus} />

        {loading ? (
          <div className="empty">Đang tải…</div>
        ) : tab === 'detail' ? (
          <div className="fin-report-cols">
            <div>
              <h3 style={{ margin: '4px 0 10px' }}>💳 Công nợ COD chưa thu</h3>
              <DetailTable items={detail?.items} total={detail?.total} />
            </div>
            <div>
              <h3 style={{ margin: '4px 0 10px' }}>🚚 Cước nội địa &amp; NVL</h3>
              <DomesticFeesTab toast={toast} />
            </div>
          </div>
        ) : tab === 'group' ? (
          <GroupRecoveryView groupData={group} custItems={detail?.items} toast={toast} canClose={canCloseRecovery} />
        ) : (
          <WeeklyDebtReport
            detail={detail}
            groupData={group}
            reports={reports}
            canScan={canScanDebt}
            canClose={canCloseRecovery}
            scanning={scanning}
            sending={sending}
            onScanDebt={scan}
            onSend={send}
            onOpenCustomer={(code, name) => setCustModal({ code, name })}
            onOpenReport={(w) => openReport({ _id: w.id })}
            toast={toast}
          />
        )}
      </div>

      {custModal && <CustomerDebtModal code={custModal.code} name={custModal.name} onClose={() => setCustModal(null)} />}

      {/* Xem báo cáo tuần ĐÃ CHỐT (bấm thẻ tuần cũ trên dải timeline) */}
      {active && (
        <Modal open onClose={() => setActive(null)} wide>
          <h3 style={{ marginTop: 0 }}>📌 {active.label}</h3>
          <div className="small muted" style={{ marginBottom: 10 }}>
            Chốt bởi {active.savedBy || '—'} lúc {fmtDateTime(active.createdAt)}
            {active.period?.from ? ` · quét ${fmtDate(active.period.from)} → ${fmtDate(active.period.to)}` : ''}
          </div>
          <div className="fin-report-cols">
            <div>
              <h3 style={{ margin: '8px 0' }}>Công nợ chi tiết</h3>
              <DetailTable items={active.detail} total={active.totalDebt} />
            </div>
            <div>
              <h3 style={{ margin: '8px 0' }}>Công nợ theo nhóm</h3>
              <GroupView groups={active.groups} grandTotal={(active.groups || []).reduce((a, g) => a + g.total, 0)} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function Head() {
  return (
    <div className="page-head">
      <div>
        <h2>Báo cáo tài chính</h2>
        <div className="sub">Công nợ COD chưa thu — chốt tuần vào thứ 5</div>
      </div>
    </div>
  );
}
